import { apiClient } from './apiClient';

/**
 * Unified service for all call operations (replaces separate callService and twilioCallService)
 */
export class UnifiedCallService {
  constructor() {
    this.baseUrl = '/calls';
  }

  /**
   * Create a new call record and initiate Twilio call
   * @param {Object} callData - Call data including agentId, phoneNumber, etc.
   * @returns {Promise<Object>} Created call with Twilio call SID
   */
  async createCall(callData) {
    try {
      const response = await apiClient.post(`${this.baseUrl}`, callData);
      return response.data || response
    } catch (error) {
      console.error('Error creating call:', error);
      throw error;
    }
  }

  /**
   * End an active call
   * @param {string} callId - Internal call ID
   * @returns {Promise<Object>} End call response
   */
  async endCall(callId) {
    try {
      console.log('Making PATCH request to:', `${this.baseUrl}/${callId}/end`);
      const response = await apiClient.patch(`${this.baseUrl}/${callId}/end`);
      console.log('End call response received:', response);
      return response.data || response
    } catch (error) {
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get call by ID
   * @param {string} callId - Call ID
   * @returns {Promise<Object>} Call details
   */
  async getCall(callId) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${callId}`);
      return response.data || response
    } catch (error) {
      console.error('Error getting call:', error);
      throw error;
    }
  }

  /**
   * Update call data
   * @param {string} callId - Call ID
   * @param {Object} data - Call data to update
   * @returns {Promise<Object>} Updated call
   */
  async updateCall(callId, data) {
    try {
      const response = await apiClient.put(`${this.baseUrl}/${callId}`, data);
      return response.data || response
    } catch (error) {
      console.error('Error updating call:', error);
      throw error;
    }
  }

  /**
   * Get calls with optional filters
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} Call list with pagination info
   */
  async getCalls(params = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}`, { params });
      return response.data || response
    } catch (error) {
      console.error('Error getting calls:', error);
      throw error;
    }
  }

  /**
   * Update call status
   * @param {string} callId - Call ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated call
   */
  async updateCallStatus(callId, status) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${callId}/status`, { status });
      return response.data || response
    } catch (error) {
      console.error('Error updating call status:', error);
      throw error;
    }
  }

  /**
   * Add note to call
   * @param {string} callId - Call ID
   * @param {string} note - Note content
   * @returns {Promise<Object>} Updated call
   */
  async addCallNote(callId, note) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${callId}/notes`, { note });
      return response.data || response
    } catch (error) {
      console.error('Error adding call note:', error);
      throw error;
    }
  }

  /**
   * Update call outcome
   * @param {string} callId - Call ID
   * @param {Object} outcome - Outcome data
   * @returns {Promise<Object>} Updated call
   */
  async updateCallOutcome(callId, outcome) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${callId}/outcome`, outcome);
      return response.data || response
    } catch (error) {
      console.error('Error updating call outcome:', error);
      throw error;
    }
  }

  /**
   * BACKUP: Mock Implementation for Demo/Testing
   */
  getSimulatedCallData(scenario = 'denial', claimData, template) {
    const transcripts = DEMO_TRANSCRIPTS[scenario] || DEMO_TRANSCRIPTS.denial;
    
    const dataCollectedByScenario = {
      denial: {
        denialReason: 'Prior Authorization Required - Metformin 1000mg dosage not pre-authorized',
        denialCategory: 'Prior Authorization Required',
        denialAmount: claimData?.billedAmount ? claimData.billedAmount * 0.4 : 500,
        shortPayAmount: 0,
        resolutionPath: 'Submit prior authorization request with prescriber clinical documentation - expected response in 2-3 business days',
        nextSteps: [
          'Contact prescriber (Dr. Smith) for clinical documentation',
          'Submit PA request to insurance using prescription reference #RX123456',
          'Follow up by November 6th if no response',
          'Reprocess claim immediately upon PA approval',
          'Send reimbursement to patient once approved'
        ],
        adjustorContact: 'Sarah Johnson',
        adjustorPhone: '(555) 123-4567',
        requestId: 'PA-REQ-2025-001',
        confidence: 0.96,
        callDuration: '2 minutes 45 seconds'
      },
      shortpay: {
        denialReason: 'Patient Deductible Not Met - $500 patient responsibility',
        denialCategory: 'Plan/Coverage Limitations - Deductible',
        denialAmount: 0,
        shortPayAmount: claimData?.billedAmount ? claimData.billedAmount * 0.4 : 500,
        resolutionPath: 'Patient responsibility - Communicate deductible requirement to patient for future claims',
        nextSteps: [
          'Bill patient $500 for their deductible responsibility',
          'Add note to patient account: Deductible remaining $1,000',
          'Track future claims toward deductible fulfillment',
          'Once deductible is met, future claims will be covered at plan percentage',
          'Send EOB to patient explaining deductible'
        ],
        adjustorContact: 'Michael Chen',
        adjustorPhone: '(555) 987-6543',
        deductibleInfo: {
          annual: 1500,
          applied: 500,
          remaining: 1000
        },
        confidence: 0.94,
        callDuration: '2 minutes 15 seconds'
      }
    };

    return {
      scenario,
      transcript: transcripts,
      dataCollected: dataCollectedByScenario[scenario] || dataCollectedByScenario.denial,
      claimData: claimData,
      template: template
    };
  }
}

// Export an instance of the UnifiedCallService for real API calls
export const unifiedCallService = new UnifiedCallService();

/**
 * BACKUP: Mock Implementation for Demo/Testing
 * 
 * In production, the above UnifiedCallService integrates with:
 * - Backend API for call management
 * - Twilio for real calls
 * - Firebase for call data persistence
 */

// Simulated transcript data for different claim scenarios
const DEMO_TRANSCRIPTS = {
  denial: [
    { speaker: 'ai', text: 'Hello, this is the pharmacy calling regarding claim CLM-2025-001. May I speak with the adjustor handling this claim?', timestamp: new Date(Date.now() - 30000), confidence: 0.95 },
    { speaker: 'user', text: 'Yes, this is the adjustor. How can I help you?', timestamp: new Date(Date.now() - 27000), confidence: 0.90 },
    { speaker: 'ai', text: 'I\'m calling to follow up on the status of claim CLM-2025-001 for patient John Doe. Can you provide me with the current status?', timestamp: new Date(Date.now() - 24000), confidence: 0.93 },
    { speaker: 'user', text: 'Let me look that up... The claim was actually denied due to prior authorization not being obtained for the medication.', timestamp: new Date(Date.now() - 21000), confidence: 0.92 },
    { speaker: 'ai', text: 'I see. Prior authorization was not obtained. Can you tell me which specific medication required the prior authorization?', timestamp: new Date(Date.now() - 18000), confidence: 0.94 },
    { speaker: 'user', text: 'It\'s for the Metformin 1000mg twice daily. Our plan requires PA for this dosage level.', timestamp: new Date(Date.now() - 15000), confidence: 0.91 },
    { speaker: 'ai', text: 'Thank you for that information. What is the process to obtain the prior authorization now? Can we resubmit?', timestamp: new Date(Date.now() - 12000), confidence: 0.93 },
    { speaker: 'user', text: 'Yes, you can request PA with the prescriber\'s clinical documentation. We typically respond within 2-3 business days.', timestamp: new Date(Date.now() - 9000), confidence: 0.92 },
    { speaker: 'ai', text: 'Perfect. So if we submit the prior authorization request by end of business today, we should expect a response by November 6th?', timestamp: new Date(Date.now() - 6000), confidence: 0.94 },
    { speaker: 'user', text: 'That\'s correct. Once approved, we can reprocess the claim immediately.', timestamp: new Date(Date.now() - 3000), confidence: 0.93 },
  ],
  shortpay: [
    { speaker: 'ai', text: 'Hello, I\'m calling from the pharmacy regarding claim CLM-2025-002. Do you have a moment to discuss the payment we received?', timestamp: new Date(Date.now() - 25000), confidence: 0.94 },
    { speaker: 'user', text: 'Sure, what\'s the issue?', timestamp: new Date(Date.now() - 23000), confidence: 0.88 },
    { speaker: 'ai', text: 'Our records show we billed $1,250.00 and we\'ve only received payment of $750.00. Can you explain the $500 difference?', timestamp: new Date(Date.now() - 20000), confidence: 0.96 },
    { speaker: 'user', text: 'Yes, we paid based on our allowed amount. The remaining $500 is the patient\'s responsibility due to deductible.', timestamp: new Date(Date.now() - 17000), confidence: 0.93 },
    { speaker: 'ai', text: 'I see. So the entire $500 difference is patient deductible that hasn\'t been met?', timestamp: new Date(Date.now() - 14000), confidence: 0.95 },
    { speaker: 'user', text: 'That\'s correct. The patient hasn\'t met their $1,500 annual deductible yet. Once they do, our coverage will apply.', timestamp: new Date(Date.now() - 11000), confidence: 0.94 },
    { speaker: 'ai', text: 'Thank you for clarifying. We\'ll communicate this to the patient and update our records accordingly.', timestamp: new Date(Date.now() - 8000), confidence: 0.92 },
    { speaker: 'user', text: 'Great. Feel free to call us if you have any other questions.', timestamp: new Date(Date.now() - 5000), confidence: 0.91 },
  ]
};


// Uncomment below to use mock implementation instead of real API
// export const unifiedCallService = mockUnifiedCallService;